<template>
  <block
    component-name="block-text-product-details"
    body-class="grid grid-cols-12 gap-x-ui gap-y-3xl"
  >
    <template v-slot:default="blockData">
      <!-- FINISHES -->
      <div
        class="col-span-full flex flex-col w-full gap-3xl"
        :class="{
          'laptop:col-span-6 ': $attrs.data.body.downloads?.items?.length,
        }"
      >
        <section class="flex flex-col w-full gap-lg">
          <header class="flex flex-col w-full gap-lg">
            <wrapper-reveal reveal="fade-from-right" class="w-full">
              <h1
                v-html="$t.labels.finishes"
                class="w-full text-heading-5"
              ></h1>
            </wrapper-reveal>
            <menu
              class="flex w-full max-w-full overflow-x-auto list-none hide-scrollbars gap-md"
            >
              <wrapper-reveal
                reveal="fade-from-bottom"
                class="w-full max-w-full"
              >
                <div
                  class="flex items-baseline justify-start h-10 max-w-full overflow-x-auto gap-2xl"
                >
                  <btn-tab
                    v-for="(item, index) in tableData"
                    :title="item.title"
                    @click="currentTabIndex = index"
                    :pressed="currentTabIndex === index"
                    class="h-full"
                  />
                </div>
                <divider />
              </wrapper-reveal>
            </menu>
          </header>

          <wrapper-reveal
            v-if="thead && tbody"
            reveal="unveil-from-left"
            class="w-full max-w-full"
          >
            <table-format-tickness
              class="w-full max-w-full"
              :thead="thead"
              :tbody="tbody"
            />
          </wrapper-reveal>
        </section>

        <!-- SPECS -->
        <section
          v-if="$attrs.data.body.specs?.items?.length"
          class="flex flex-col w-full gap-md"
        >
          <wrapper-reveal reveal="fade-from-right">
            <h1 v-html="$t.labels.specs" class="text-body"></h1>
          </wrapper-reveal>

          <div class="flex flex-wrap max-w-full gap-lg">
            <media
              v-for="item in $attrs.data.body.specs.items"
              v-bind="item.asset"
              :loading="blockData?.loading || 'eager'"
              :sizes="{
                base: 100,
              }"
              :use-bg="false"
              reveal="unveil-from-right"
              class="w-[50px]"
            />
          </div>
        </section>
        <!-- end SPECS -->
      </div>
      <!-- end FINISHES -->

      <!-- DOWNLOADS -->
      <div
        v-if="$attrs.data.body.downloads?.items?.length"
        class="flex flex-col w-full col-span-full laptop:col-start-8 laptop:col-end-12 gap-2xl"
      >
        <btn-list
          size="small"
          :asset="
            $attrs.data.body.downloads?.asset
              ? $attrs.data.body.downloads.asset
              : undefined
          "
          :title="
            $attrs.data.body.downloads?.title
              ? $attrs.data.body.downloads.title
              : undefined
          "
          :items="$attrs.data.body.downloads?.items"
          class="w-full"
        />
      </div>
      <!-- end DOWNLOADS -->
    </template>
  </block>
</template>

<script setup>
const currentTabIndex = ref(0);
watch(currentTabIndex, () => {
  ScrollTrigger.refresh(true);
});

const attrs = useAttrs();
const nuxtApp = useNuxtApp();

const tableData = ref([...attrs.data.body.finishes.table_data]);

tableData.value.forEach((item) => {
  const uniqFormats = [];
  for (let i = 0; i < item.formats.length; i++) {
    if (
      !uniqFormats.find(
        (format) => JSON.stringify(format) === JSON.stringify(item.formats[i])
      )
    ) {
      uniqFormats.push(item.formats[i]);
    }
  }
  item.formats = uniqFormats;
});

tableData.value.forEach((item) => {
  item.formats.forEach((format) => {
    // extract only numxnum from cm, remove space and letters
    let numxnum = format.cm.match(/\d+x\d+/g);

    if (!numxnum) {
      format.size = 0;
      return;
    }

    numxnum = numxnum[0];

    const splitcm = numxnum.split("x");
    format.size = splitcm[0] * splitcm[1];
  });
});

const thead = computed(() => {
  if (!tableData.value.length) {
    return;
  }

  const ticknesses = [];
  tableData.value.forEach((item) => {
    item.formats.forEach((format) => {
      format.ticknesses.forEach((tickness) => {
        ticknesses.push(`${tickness["mm"]} mm`);
      });
    });
  });

  const uniqueSpessori = [...new Set(ticknesses)].sort();
  return [nuxtApp.$t.labels.format_tickness].concat(uniqueSpessori);
});

const tbody = computed(() => {
  if (!tableData.value.length) {
    return;
  }

  const finishes = [];
  tableData.value.forEach((item) => {
    item.formats.forEach((format) => {
      finishes.push({
        size: format.size,
        text: format.size
          ? `${format.cm} cm <span style='font-size: 0.9em;'> / ${format.in} in</span>`
          : `${format.cm} <span style='font-size: 0.9em;'> / ${format.in} </span>`,
        cm: format.cm,
      });
    });
  });

  const uniqueFinishes = [...new Set(finishes)].sort((a, b) => {
    return a.size - b.size;
  });
  /* .map((item) => {
      return item.text;
    }) */

  const ticknesses = [];
  tableData.value.forEach((item) => {
    item.formats.forEach((format) => {
      format.ticknesses.forEach((tickness) => {
        ticknesses.push(tickness["mm"]);
      });
    });
  });

  const uniqueSpessori = [nuxtApp.$t.labels.format_tickness].concat(
    [...new Set(ticknesses)].sort()
  );

  let _tbody = {};
  uniqueFinishes.forEach((item) => {
    _tbody[item.cm] = [item.text];
  });

  function getPointType(tickness) {
    return tickness.resource ? "resource" : true;
  }

  tableData.value[currentTabIndex.value].formats.forEach((item) => {
    item.ticknesses.forEach((tickness) => {
      for (let i = 1; i < uniqueSpessori.length; i++) {
        _tbody[item.cm][i] =
          _tbody[item.cm][i] ||
          (tickness["mm"] == uniqueSpessori[i]
            ? getPointType(tickness)
            : false);
      }
    });
  });

  _tbody = Object.keys(_tbody).map((key) => {
    return _tbody[key];
  });

  // each array must have the same length
  const maxLength = Math.max(..._tbody.map((item) => item.length));
  _tbody.forEach((item) => {
    while (item.length < maxLength) {
      item.push(false);
    }
  });

  return _tbody;
});
</script>
